/**
 * therapyConfigurationMiddleware — broadcasts therapy-config changes to the
 * socket AFTER the reducer has updated state (DEBT-1 fix).
 *
 * Previously therapyConfigurationSlice reducers called wsapi().emit(...)
 * directly, which is a side effect inside a "pure" reducer — it broke under
 * React 18 StrictMode's double-invocation of reducers in dev, and made the
 * slice impossible to unit test without a live socket.
 *
 * This listener middleware preserves the exact previous behaviour:
 * - Only broadcasts when the current URL is a host session (`/session-...`),
 *   never on the visitor/patient path (`/csession-...`), matching the old
 *   `sendUpdateToClients` guard. This also prevents an echo loop when a
 *   visitor receives `configurationChange` and dispatches
 *   `setTherapyConfiguration` locally (that dispatch must NOT re-broadcast).
 */

import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { wsapi } from '../components/api/wsapi';
import {
    setTherapyConfiguration,
    setSpeed,
    setSets,
    setCycles,
    increaseSets,
    increaseCycles,
    setState,
    setDirectionChange,
    setLightSize,
    setLightShape,
    setLightPath,
    setColorPreset,
} from '../components/pages/therapy-session/therapyConfigurationSlice';
import type { RootState } from './index';

export const therapyConfigurationMiddleware = createListenerMiddleware();

therapyConfigurationMiddleware.startListening({
    matcher: isAnyOf(
        setTherapyConfiguration,
        setSpeed,
        setSets,
        setCycles,
        increaseSets,
        increaseCycles,
        setState,
        setDirectionChange,
        setLightSize,
        setLightShape,
        setLightPath,
        setColorPreset,
    ),
    effect: (_action, listenerApi) => {
        if (!document.location.href.includes('/session-')) {
            // Visitor path (/csession-) or unrelated route — never broadcast from here.
            return;
        }

        const state = (listenerApi.getState() as RootState).therapyConfiguration;
        const sessionId = document.location.href.split('-').pop();

        wsapi().emit('changeTherapyConfiguration', {
            session: 'session-' + sessionId,
            therapyConfiguration: {
                speed: state.speed,
                sets: state.sets,
                cycles: state.cycles,
                state: state.state,
                directionChange: state.directionChange,
                lightSize: state.lightSize,
                lightShape: state.lightShape,
                lightPath: state.lightPath,
                colorPreset: state.colorPreset,
            },
        });
    },
});
